import {FC, useEffect, useRef} from 'react'
import classNames from 'classnames'
import {usePolicy, useToggle} from 'hooks'
import {Link} from 'react-router-dom'
import {ChevronRightIcon} from '../SvgIcon'
import {stringSplitAndJoin} from 'utils'

const routes = [
  {name: 'Overview', path: '/analytics/overview'},
  {name: 'Policy', path: '/analytics/policy'},
  {name: 'Productivity', path: '/analytics/productivity'},
  {name: 'Shadow IT', path: '/analytics/shadowit'},
  {name: 'Detail', path: '/analytics/detail'},
]

interface Props {
  bgColor: string
  textColor: string
  text: string
  bgOpacity: string
  bgHoverOpacity: string
  className?: string
}

export const AnalyticsRouteDropDown: FC<Props> = ({
  bgColor,
  text,
  textColor,
  bgOpacity,
  bgHoverOpacity,
  className,
}) => {
  const {pathname} = usePolicy()
  const {toggleOn, onToggleClick} = useToggle()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // to determine clicked target and close the dropdown
    const checkIfClickedOutside = (event: MouseEvent) => {
      if (
        toggleOn &&
        ref.current &&
        !ref.current.contains(event.target as Node)
      ) {
        onToggleClick()
      }
    }

    // adding event listener to know whenever user clicks on page
    document.addEventListener('mousedown', checkIfClickedOutside)

    // cleanup function to remove event listener on unmount
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [toggleOn])

  return (
    <div className="relative" ref={ref}>
      <button
        data-cy="analytics-dropdown"
        onClick={onToggleClick}
        tabIndex={0}
        className={classNames(
          // Combine all the dynamic classes as a single string rather than multiple arguments
          `bg-${bgColor} bg-${bgOpacity} hover:bg-${bgHoverOpacity} border-${bgColor}`,
          // Fixed classes
          'outline-none rounded-full flex items-center justify-center border focus-visible:border-white px-4 py-1 ml-3',
          // Separate argument for the custom className prop
          className,
        )}
      >
        <p className={`text-sm karla-medium ${textColor}`}>{text}</p>
        <ChevronRightIcon
          strokeColor={textColor}
          classes={classNames('transform rotate-90 ml-4', {
            '-rotate-90': toggleOn,
          })}
          width={6}
          height={9}
        />
      </button>

      <div
        className={classNames(
          'absolute z-50 bg-dope-black border border-gray-600 flex flex-col w-40 mt-3 mx-auto',
          {
            hidden: toggleOn === false,
          },
        )}
      >
        {routes.map((data, idx) => (
          <Link
            className="w-full"
            to={data.path}
            key={`analytics-route-item-${idx}`}
          >
            <button
              data-cy={`analytics-dropdown-option-${stringSplitAndJoin(
                data?.name?.toLowerCase(),
              )}`}
              className={classNames(
                'w-full py-2 karla-light text-sm hover:bg-gray-800',
                {
                  'border-b border-gray-700': idx !== routes.length - 1,
                  [textColor]: data.path === pathname,
                },
              )}
            >
              {data.name}
            </button>
          </Link>
        ))}
      </div>
    </div>
  )
}
